// Copyright (c) 2026 Anchorbase, Inc. All rights reserved.

import { z } from 'zod';
import {
    WINDOWS_INSTALLER_PUBLISHED_FILE_NAME,
    WINDOWS_INSTALLER_QA_PUBLISHED_FILE_NAME,
    type WindowsArtifactQa,
    type WindowsArtifactQaChecksV1,
    type WindowsArtifactQaV1,
    type WindowsPeQaEvidenceV1,
} from '../windows-artifact-qa.types';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
} from '../stored-json-contract.types';

const Sha256Schema: z.ZodType<string> = z.string().regex(/^[0-9a-f]{64}$/);

export const WindowsPeQaEvidenceV1Schema: z.ZodType<WindowsPeQaEvidenceV1> = z
    .object({
        relativePath: z.string().min(1),
        role: z.enum(['installer', 'portable', 'main_executable', 'uninstaller', 'bundled']),
        peKind: z.enum(['executable', 'dll']),
        peArchitecture: z.string().min(1),
        companyName: z.string().min(1).nullable(),
        productName: z.string().min(1).nullable(),
        owner: z.enum(['anchorbase', 'third_party', 'unknown']),
        signatureClassification: z.enum([
            'anchorbase_signed',
            'trusted_third_party_signed',
            'unsigned',
            'invalid_or_untrusted',
        ]),
        signatureStatus: z.string().min(1),
        publisher: z.string().min(1).nullable(),
        signerSubject: z.string().min(1).nullable(),
        signerThumbprint: z
            .string()
            .regex(/^[0-9A-F]{40}$/)
            .nullable(),
        timestamped: z.boolean(),
        prePackagingSha256: Sha256Schema.nullable(),
        finalSha256: Sha256Schema,
        requiredAction: z.enum(['preserve', 'anchorbase_sign', 'reject']),
    })
    .strict();

export const WindowsArtifactQaChecksV1Schema: z.ZodType<WindowsArtifactQaChecksV1> = z
    .object({
        installerSigned: z.boolean(),
        installerTimestamped: z.boolean(),
        installerPublisherMatches: z.boolean(),
        portableSigned: z.boolean(),
        mainExecutableSigned: z.boolean(),
        uninstallerSigned: z.boolean(),
        allPackagedPeFilesSigned: z.boolean(),
    })
    .strict();

export const WindowsArtifactQaV1Schema: z.ZodType<WindowsArtifactQaV1> = z
    .object({
        schemaVersion: z.literal(1),
        result: z.enum(['pass', 'fail']),
        artifact: z
            .object({
                fileName: z.string().min(1),
                publishedFileName: z.literal(WINDOWS_INSTALLER_PUBLISHED_FILE_NAME),
                qaFileName: z.string().min(1),
                publishedQaFileName: z.literal(WINDOWS_INSTALLER_QA_PUBLISHED_FILE_NAME),
                architecture: z.enum(['x64', 'arm64']),
                track: z.enum(['stable', 'latest']),
                version: z.string().min(1),
                buildNumber: z.string().regex(/^\d+$/),
                commitId: z.string().regex(/^[0-9a-f]{40}$/),
                installerSha256: Sha256Schema,
            })
            .strict(),
        checks: WindowsArtifactQaChecksV1Schema,
        files: z.array(WindowsPeQaEvidenceV1Schema).min(1),
    })
    .strict();

export const WindowsArtifactQaContract = defineStoredJsonContract({
    contractName: 'WindowsArtifactQa',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'WindowsArtifactQaV1Schema',
            schema: WindowsArtifactQaV1Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['schemaVersion'],
                    markerValue: 1,
                },
            ],
            normalize: (value: WindowsArtifactQaV1): WindowsArtifactQa => value,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const WindowsArtifactQaSchema: z.ZodType<WindowsArtifactQa> =
    buildStoredJsonValueParser(WindowsArtifactQaContract);

export function serializeWindowsArtifactQaV1(value: WindowsArtifactQa): WindowsArtifactQaV1 {
    return WindowsArtifactQaV1Schema.parse(value);
}
